import React, { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { theme } from '../../domain/theme';
import { conf, t } from '../../domain/services/configService';
import { eventService } from '../../domain/services/eventService';
import { breakpointService } from '../../domain/services/breakpointService';
import { imageResizerService } from '../../domain/services/imageResizerService';
import { getHubPage, getTaxonomies } from '../../domain/utils/contentUtils';
import { Ad } from '../../components/responsive/atoms/Ad';
import { ArticleImage } from '../../components/ArticleImage';
import ShareKit from '../../components/ShareKit';
import { ArticleVideo } from '../../components/ArticleVideo';
import { Content } from '../../components/Paragraph/Content';
import { Content as ContentNoLazyLoadable } from '../../components/Paragraph/Content/indexNoLazyLoadable';
import { ContentPackage } from '../../components/Paragraph/ContentPackage/ContentPackage';
import { VideoModule } from '../../components/Paragraph/VideoModule/VideoModule';
import { VideoPlaylist } from '../../components/Paragraph/VideoPlaylist/VideoPlaylist';
import { VideoPlaylistContent } from '../../components/VideoPlaylistContent';
import { NowOnTv } from '../../components/NowOnTv';
import { InlineGallery } from '../../components/responsive/organisms/InlineGallery';
import { YoutubeVideoParagraph } from '../../components/Paragraph/YoutubeVideoParagraph';
import { PullQuote } from '../../components/Paragraph/PullQuote';
import { InlinePromosParagraph } from '../../components/Paragraph/InlinePromosParagraph';
import { ContentHtml } from '../../components/Paragraph/ContentHtml';
import { PromoParagraph } from '../../components/Paragraph/PromoParagraph';
import { PromoBannerParagraph } from '../../components/Paragraph/PromoBannerParagraph';
import { SubscriptionBlock } from '../../components/SubscriptionBlock/SubscriptionBlock';
import { MostPopularContent } from '../../components/MostPopularContent';
import { RelatedVideos } from '../../components/responsive/organisms/RelatedVideos';
import { ContentMainTitle } from '../../components/ContentMainTitle';
import { TagList } from '../../components/responsive/molecules/TagList';
import { SponsoredBar } from '../../components/SponsoredBar';
import { SponsoredAdditionalText } from '../../components/SponsoredAdditionalText';
import { AmbientPlayerModule } from '../../components/Paragraph/AmbientPlayerModule';
import { ImmersiveLead } from '../../components/Paragraph/ImmersiveLead';
import { CallToActionButton } from '../../components/Paragraph/CallToActionButton';
import { ImageGroup } from '../../components/Paragraph/ImageGroup';
import { NewsletterModuleParagraph } from '../../components/Paragraph/NewsletterModuleParagraph/NewsletterModuleParagraph';
import { ImageCarousel } from '../../components/Paragraph/ImageCarousel/ImageCarousel';
import { TableOfContentsParagraph } from '../../components/Paragraph/TableOfContentsParagraph';
import { Actionable } from '../../components/responsive/atoms/Actionable';
import { PromoGridParagraph } from '../../components/Paragraph/PromoGridParagraph';
import { Recommendation } from '../../components/Recommendation';
import { AnimalProfile } from '../../components/Paragraph/AnimalProfile';
import { AuthorBoxNotes } from '../../components/Paragraph/AuthorBoxNotes';

const AD_INTERVAL = 900;

const getStyles = state => ({
  articleContainer: css`
    position: relative;
    width: 100%;
    max-height: ${state.expanded ? 'none' : '35vh'};
    overflow: ${state.expanded ? 'visible' : 'hidden'};
    ${theme.mq.tablet} {
      max-height: none;
      overflow: visible;
      & .ngart__mobile-ad {
        display: none;
      }
    }
    &:after {
      display: ${state.expanded ? 'none' : 'block'};
      ${theme.mq.tablet} {
        display: none;
      }
      content: ' ';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 120px;
      background: linear-gradient(
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.25) 10%,
        rgba(255, 255, 255, 0.65) 50%,
        rgba(255, 255, 255, 0.87) 95%,
        rgba(255, 255, 255, 0.96) 100%
      );
    }
  `,

  article: css`
    margin-bottom: 60px;
  `,

  gridWrapper: css`
    max-width: 68em;
    margin: 0 30px;
    display: flex;

    &::after {
      clear: both;
      content: '';
      display: table;
    }

    & .grid-full {
      margin-left: -30px;
      margin-right: -30px;
    }

    ${theme.mq.tablet} {
      margin: 0 40px;
      & .grid-full {
        margin-left: -40px;
        margin-right: -40px;
      }
    }

    ${theme.mq.small_desktop} {
      max-width: none;
      margin: 0 60px;

      & .grid-full {
        margin-left: -60px;
        margin-right: -60px;
      }
    }

    ${theme.mq.large_desktop} {
      padding: 0;
      width: 100%;
      max-width: 1160px;
      margin: 0 auto;
    }

  }
  `,

  articleMainColumn: css`
    width: 100%;
    display: inline-block;

    & blockquote,
    & div.paragraph p,
    & div.paragraph ul,
    & div.paragraph ol {
      ${theme.font.family('paragraphs')};
      font-size: ${theme.font.size('l')};
      line-height: 1.9375rem;
      letter-spacing: normal;
    }

    & h2 a,
    & h3 a {
      color: #000;
    }

    & h5 {
      ${theme.font.family('boldWeb')};
      font-size: 1.125rem;
      letter-spacing: 2.5px;
      line-height: 1.5;
      color: #000;
      font-weight: 700;
      text-transform: uppercase;
    }

    & blockquote,
    & p {
      margin-bottom: 30px;
      color: #333;
    }

    & blockquote a,
    & p a,
    & ul a,
    & ol a {
      color: #000;
      text-decoration: none;
      border-bottom: 2px solid #fc0;
    }

    & div.paragraph ul,
    & div.paragraph ol {
      margin: 20px 0;
    }

    & ul li {
      list-style: disc;
      list-style-position: inside;
    }

    & ol li {
      list-style: decimal;
      list-style-position: inside;
    }

    & .paragraph h2,
    & .paragraph h3,
    & p h2,
    & p h3 {
      ${theme.font.family('boldWeb')};
    }

    ${theme.mq.tablet} {
      & .paragraph,
      & p {
        margin-bottom: 30px;
      }

      & .paragraph h2,
      & p h2 {
        font-weight: 400;
        font-size: 1.5rem;
        letter-spacing: 0.5px;
        line-height: 1.5;
      }
    }

    ${theme.mq.small_desktop} {
      width: calc(100% - 320px);
      padding-right: 50px;
      float: left;
      flex: 0 1 auto;
      & .ngart-img--large .ngart-img__cont__copy {
        margin-right: unset;
      }
    }
  `,

  articleSideColumn: css`
    display: none;
    ${theme.mq.small_desktop} {
      display: flex;
      width: 300px;
      float: left;
      flex: 0 1 auto;
      flex-direction: column;
    }
  `,

  articleSideInner: css`
    top: 0;
    transition: top 0.5s ease-in-out;
    position: sticky;
  `,

  articleSubColumn: css`
    ${theme.mq.small_desktop} {
      width: 100%;
      flex: 1;
    }
  `,

  ngartAdCol: css`
    ${theme.mq.small_desktop} {
      width: 100%;
      flex: 0 1 auto;
      margin-bottom: 8px;
    }
  `,

  ngartFooterAd: css`
    margin-top: 20px;
    max-width: 1060px;
    text-align: center;
    margin-bottom: 30px;
  `,

  ngartSideAd: css`
    width: 100%;
    overflow: hidden;
    top: 100px;
    left: 0;
    height: auto;

    &:after,
    &:before {
      content: '';
      display: table;
    }
  `,

  tagList: css`
    display: ${state.expanded ? 'block' : 'none'};
    ${theme.mq.tablet} {
      display: block;
    }
  `,

  readMore: css`
    display: ${state.expanded ? 'none' : 'block'};
    ${theme.mq.tablet} {
      display: none;
    }
    ${theme.font.family('boldWeb')};
    font-weight: 400;
    letter-spacing: 3px;
    text-transform: uppercase;
    line-height: 1.875rem;
    text-align: center;
    background: #fc0;
    color: #000;
    padding: 7px 0;
    font-size: 0.875rem;
    padding: 10px 5px;
    margin-top: 30px;
    cursor: pointer;
    &:hover {
      color: #fff;
      background: #222;
    }
  `,
  relatedVideos: css`
    display: ${state.expanded ? 'block' : 'none'};
    ${theme.mq.tablet} {
      display: block;
    }
  `
});

const largeParagraphsTypes = [
  'ParagraphContentPackage',
  'ParagraphFocusedHeroPackage',
  'ParagraphVideoModule',
  'ParagraphLeftAndRightContentPackage',
  'ParagraphTvGuideElement',
  'ParagraphPromoGrid'
];

const isLargeParagraph = paragraph => {
  const e = paragraph?.entity;
  if (largeParagraphsTypes.includes(e?.type || '')) {
    return true;
  }
  if (
    (e?.type === 'ParagraphImageGroup' && e?.imagesSize === 'full-width') ||
    (e?.type === 'ParagraphImageCarousel' && e?.widthSize === 'large') ||
    (e?.type === 'ParagraphImageToolkitElement' && e?.size === 'large') ||
    (e?.type === 'ParagraphAmbientPlayerModule' &&
      e?.videoPlayerSize === 'large') ||
    (e?.type === 'ParagraphArticleVideoPlaylist' &&
      ['large', 'full'].includes(e?.widthSize))
  ) {
    return true;
  }
  return false;
};

/* HeroImage used in the very top of articles, outside the main narrow column. */
const getHeroImage = (content, isAboveTheFoldSet) => {
  if (content.mediaType === 'photo' && content.image?.entity?.mediaImage?.url) {
    let articleImageIsAboveTheFold = false;
    if (!isAboveTheFoldSet) {
      // eslint-disable-next-line no-param-reassign
      isAboveTheFoldSet = true;
      articleImageIsAboveTheFold = true;
    }
    return (
      <ArticleImage
        className={`module--type-PhotoHeroImage module--id-${content.id}`}
        imageEntity={imageResizerService.overrideImageFocalPoint(content.image)}
        size="large"
        aspect="16x9"
        isAboveTheFold={articleImageIsAboveTheFold}
        preload={articleImageIsAboveTheFold}
      />
    );
  }
  return null;
};

const getParagraphContent = (content, paragraph, ads, isAboveTheFold) => {
  if (!paragraph.entity) {
    return null;
  }

  const contentHasToc = content.mainContent.some(
    para => para.entity.type === 'ParagraphTableOfContents'
  );
  const ContentComponent = contentHasToc ? ContentNoLazyLoadable : Content;

  const key = `${content.id}-${paragraph.entity.type}_${paragraph.entity.id}`;
  switch (paragraph.entity.type) {
    case 'ParagraphContent':
      return (
        <ContentComponent
          data={paragraph.entity}
          content={content}
          ads={ads}
          key={key}
        />
      );
    case 'ParagraphContentPackage':
      return (
        <ContentPackage
          data={paragraph.entity}
          content={content}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      );
    case 'ParagraphArticleVideoPlaylist':
      return (
        <VideoPlaylist
          data={paragraph.entity}
          bundle={content.bundle}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      );
    case 'ParagraphVideoModule':
      return (
        <VideoModule
          data={paragraph.entity}
          content={content}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      );
    case 'ParagraphImageToolkitElement': {
      return paragraph.entity.image?.entity ? (
        <ArticleImage
          className={`module--type-${paragraph.entity.type} module--id-${paragraph.entity.id}`}
          imageEntity={imageResizerService.overrideImageFocalPoint(
            paragraph.entity.image
          )}
          size={paragraph.entity.size}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      ) : null;
    }
    case 'ParagraphGalleryInlineElement': {
      return paragraph.entity.photoGallery?.entity?.images ? (
        <InlineGallery
          data={paragraph.entity}
          galleryId={paragraph.entity.photoGallery.id}
          imageEntities={paragraph.entity.photoGallery.entity.images}
          content={content}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      ) : null;
    }
    case 'ParagraphVideoToolkit': {
      return paragraph.entity.video?.entity ? (
        <ArticleVideo
          data={paragraph.entity}
          content={content}
          isParagraph
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      ) : null;
    }
    case 'ParagraphPullQuote':
      return <PullQuote {...paragraph.entity} key={key} />;
    case 'ParagraphCallToActionButton':
      return (
        <CallToActionButton
          url={paragraph.entity.ctaButtonLink.url?.path}
          text={paragraph.entity.ctaButtonText}
          openInNewTab={paragraph.entity.ctaOpenInNewTab}
          key={key}
        />
      );
    case 'ParagraphYoutubeVideo':
      return (
        <YoutubeVideoParagraph
          data={paragraph.entity}
          videoId={paragraph.entity.videoId}
          key={key}
        />
      );
    case 'ParagraphContentHtml':
      return <ContentHtml data={paragraph.entity} key={key} />;
    case 'ParagraphInlinePromos':
      return <InlinePromosParagraph {...paragraph.entity} key={key} />;
    case 'ParagraphPromo':
      return <PromoParagraph {...paragraph.entity} key={key} />;
    case 'ParagraphPromoBanner':
      return (
        <PromoBannerParagraph
          {...paragraph.entity}
          key={key}
          isAboveTheFold={isAboveTheFold}
        />
      );
    case 'ParagraphAmbientPlayerModule':
      return (
        <AmbientPlayerModule
          data={paragraph.entity}
          bundle={content.bundle}
          key={key}
        />
      );
    case 'ParagraphImageGroup':
      return <ImageGroup key={key} data={paragraph.entity} />;
    case 'ParagraphNewsletterModule':
      return <NewsletterModuleParagraph data={paragraph.entity} key={key} />;
    case 'ParagraphArticleImageCarousel':
      return (
        <ImageCarousel
          key={key}
          data={paragraph.entity}
          isAboveTheFold={isAboveTheFold}
        />
      );
    case 'ParagraphTableOfContents':
      return (
        <TableOfContentsParagraph
          data={paragraph.entity}
          content={content}
          key={key}
        />
      );
    case 'ParagraphPromoGrid':
    case 'ParagraphManualPromoGrid':
      return (
        <PromoGridParagraph
          data={paragraph.entity}
          content={content}
          key={key}
        />
      );
    case 'ParagraphAnimalProfile':
      return (
        <AnimalProfile
          data={paragraph.entity}
          content={content}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      );
    case 'ParagraphAuthorBoxNotes':
      return (
        <AuthorBoxNotes
          data={paragraph.entity}
          content={content}
          isAboveTheFold={isAboveTheFold}
          key={key}
        />
      );
    default:
      break;
  }
  return null;
};

export const ArticleInner = memo(({ content, isTransported }) => {
  const [state, setState] = useState({ ads: null, expanded: !isTransported });
  const mainColumnRefs = [];
  const articleTag = useRef(null);
  let isAboveTheFoldSet = false;
  const tags = getTaxonomies(content, { includeParents: true });
  const heroImage = getHeroImage(content, isAboveTheFoldSet);
  const hasArticleContent = content.mainContent?.length > 0;
  const styles = getStyles(state);

  const handleReadMoreClick = () => {
    const ads = {};
    // Add teads to the very first articleContent, without
    // taking into account the AD_INTERVAL
    if (mainColumnRefs.length > 0) {
      mainColumnRefs.forEach((mainColumnRef, mainColumnIndex) => {
        let y = 0;
        let adIndex = 0;

        const contentParagraphs = mainColumnRef.current.querySelectorAll(
          '.paragraph > div > p'
        );
        contentParagraphs?.forEach((paragraphEl, paragraphElIndex) => {
          const paragraphId = paragraphEl.getAttribute('data-paragraph-id');
          // Add teads to the very first articleContent, without
          // taking into account the AD_INTERVAL
          if (mainColumnIndex === 0 && paragraphElIndex === 0) {
            ads[paragraphId] = {
              adIndex: 'group0-item0',
              isTeads: true,
              isTransported
            };
          }
          const height = paragraphEl.clientHeight;
          y += height;
          if (y > AD_INTERVAL) {
            ads[paragraphId] = {
              adIndex: `group${mainColumnIndex}-item${adIndex}`,
              isTeads: false,
              isTransported
            };
            adIndex += 1;
            y = 0;
          }
        });
      });
    }
    setState({ ads, expanded: true });

    eventService.dispatchEvent(
      'readMoreButtonClicked',
      { content },
      {
        setFlag: false
      }
    );
  };

  const emulateReadMoreClick = () => {
    if (breakpointService.isMobile() && !isTransported) {
      handleReadMoreClick();
    }
  };

  useEffect(() => {
    eventService.addEventListener(
      'transporterScrollingSetDefined',
      emulateReadMoreClick,
      {
        once: true
      }
    );

    return () => {
      eventService.removeEventListener(
        'transporterScrollingSetDefined',
        handleReadMoreClick
      );
    };
  }, []);

  const shareLinkGtmData = [];

  const getContentWithHeaders = () => {
    const parts = [];
    let partsIndex = 0;
    parts[partsIndex] = {
      large: false,
      content: [],
      index: partsIndex,
      paragraphName: null
    };

    if (
      content.mediaType === 'shortform' &&
      (content.videoRef?.entity?.guid || content.videoRef?.entity?.mpxGuid)
    ) {
      shareLinkGtmData.push(
        {
          var: 'guid',
          value: content.videoRef.entity.guid || content.videoRef.entity.mpxGuid
        },
        {
          var: 'show_title',
          value: content.videoRef.entity.show?.[0]?.entity?.title
        },
        {
          var: 'tag',
          value: getHubPage(content)?.title || ''
        },
        {
          var: 'article_video_title',
          value: content.title
        }
      );

      let articleVideoIsAboveTheFold = false;
      if (!isAboveTheFoldSet) {
        isAboveTheFoldSet = true;
        articleVideoIsAboveTheFold = true;
      }
      parts[partsIndex].content.push(
        <ArticleVideo
          data={{ type: 'ParagraphVideoToolkit', id: content.id }}
          content={content}
          key={`ParagraphVideoToolkit_${content.id}`}
          isAboveTheFold={articleVideoIsAboveTheFold}
        />
      );
      // partsIndex += 1;
    }

    if (content.mediaType === 'video_playlist' && content.videoPlaylist) {
      let videoPlaylistContentIsAboveTheFold = false;
      if (!isAboveTheFoldSet) {
        isAboveTheFoldSet = true;
        videoPlaylistContentIsAboveTheFold = true;
      }
      parts[partsIndex].content.push(
        <VideoPlaylistContent
          className={`module--type-VideoPlaylistContent module--id-${content.id}`}
          videos={content.videoPlaylist.entity.videos}
          bundle={content.bundle}
          isAboveTheFold={videoPlaylistContentIsAboveTheFold}
          isStandalone
          key="VideoPlaylistContent"
        />
      );
    }

    if (content.mainContent) {
      let paragraphContentCount = 0;
      content.mainContent.map((paragraph, index) => {
        let nextParagraph;
        let large = true;
        // Check if paragraph should be full size or included in group with sidebar.
        if (!isLargeParagraph(paragraph)) {
          large = false;
        }

        // Since paragraph grouping happens at the end of this block, we need to control
        // the edge case when the first item of parts comes from a video or video playlist
        const sizeBetweenFirstAndSecondParagraphsDiffers =
          partsIndex === 0 &&
          large !== parts[partsIndex].large &&
          parts[partsIndex].content.length > 0;
        if (sizeBetweenFirstAndSecondParagraphsDiffers) {
          partsIndex += 1;
        }

        if (typeof parts[partsIndex] === 'undefined') {
          parts[partsIndex] = { large, content: [] };
        }

        parts[partsIndex].large = large;
        parts[partsIndex].index = partsIndex;
        if (paragraph.entity && paragraph.entity.type === 'ParagraphContent') {
          paragraphContentCount += 1;
        }

        let paragraphContentIsAboveTheFold = false;
        if (!isAboveTheFoldSet && partsIndex === 0) {
          // eslint-disable-next-line no-param-reassign
          isAboveTheFoldSet = true;
          paragraphContentIsAboveTheFold = true;
        }
        parts[partsIndex].content.push(
          getParagraphContent(
            content,
            paragraph,
            state.ads,
            null,
            paragraphContentIsAboveTheFold
          )
        );
        if (paragraphContentCount === 2) {
          paragraphContentCount += 1;
          parts[partsIndex].content.push(
            <MostPopularContent key={`${content.id}-MostPopularContent`} />
          );
        }
        if (index < content.mainContent.length) {
          nextParagraph = content.mainContent[index + 1];
        }
        if (
          typeof nextParagraph === 'undefined' ||
          isLargeParagraph(nextParagraph) ||
          (large && !isLargeParagraph(nextParagraph))
        ) {
          partsIndex += 1;
        }
        return null;
      });
    }

    const doNotShowMostPopularOnVideo =
      (content.mediaType === 'shortform' ||
        content.mediaType === 'video_playlist') &&
      (content.videoRef?.entity?.guid || content.videoRef?.entity?.mpxGuid) &&
      parts[0].content.length === 1;

    return (
      <>
        <div css={styles.articleContainer}>
          {parts.map(part => {
            if (!part.large) {
              mainColumnRefs.push(useRef());
            }
            return (
              <React.Fragment key={`${content.id}-${part.index}`}>
                {part.large ? (
                  <>{part.content.map(paragraphModule => paragraphModule)}</>
                ) : (
                  <div css={styles.gridWrapper}>
                    <div
                      css={styles.articleMainColumn}
                      ref={mainColumnRefs[mainColumnRefs.length - 1]}
                    >
                      {part.content.map(paragraphModule => paragraphModule)}
                    </div>
                    <div css={styles.articleSideColumn}>
                      <div css={styles.articleSideInner}>
                        <div css={styles.ngartAdCol}>
                          <div css={styles.ngartSideAd}>
                            <Ad
                              id={`article_side__${part.index}`}
                              content={content}
                              refresh
                              collapseEmptyDiv={isTransported}
                              /*
                                // Commented since we are not supporting sticky refreshes for the moment
                                // Delete sticky code once GAM is deployed to prod
                                sticky={{
                                  containerClass: 'ngart__side-col'
                                }} */
                            />
                          </div>
                        </div>
                        <div css={styles.articleSubColumn}>
                          {!doNotShowMostPopularOnVideo && (
                            <MostPopularContent location="sidebar" />
                          )}
                          {content.rightRailSubscribeBlock?.entity
                            ?.subscriptionBlock?.entity ? (
                            <SubscriptionBlock
                              id={
                                content.rightRailSubscribeBlock.entity
                                  .subscriptionBlock.entity.id
                              }
                            />
                          ) : null}
                          {content.rightRailScheduleCard && <NowOnTv />}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div
          css={[
            styles.gridWrapper,
            css`
              display: block;
              ul {
                margin-top: 30px;
                ${theme.mq.small_desktop} {
                  width: calc(100% - 320px);
                }
              }
            `
          ]}
        >
          <SponsoredAdditionalText content={content} />
          <div css={styles.tagList}>
            <TagList tags={tags} />
          </div>
          {(content.mediaType === 'shortform' ||
            content.mediaType === 'video_playlist') &&
            hasArticleContent && (
              <div css={styles.ngartFooterAd}>
                <Ad id="article_footer" content={content} />
              </div>
            )}

          <Actionable
            title={t('Read More')}
            className="ngart__read-more"
            onClick={handleReadMoreClick}
          >
            <div className="ngart__read-more" css={styles.readMore}>
              {t('Read More')}
            </div>
          </Actionable>
        </div>
      </>
    );
  };

  let immersiveLeadIsAboveTheFold = false;
  if (content.immersiveLead && !isAboveTheFoldSet) {
    isAboveTheFoldSet = true;
    immersiveLeadIsAboveTheFold = true;
  }
  return (
    <>
      {!isTransported && (
        <ShareKit
          type="article"
          content={content}
          twitterHandle={conf.menus.menuSocialMedia.twitterHandle}
          gtmData={shareLinkGtmData}
          color="black"
        />
      )}
      {content.bundle === 'article' && (
        <Ad id="teads_tablet_and_desktop" content={content} refresh={false} />
      )}
      <article
        data-node-id={content.id}
        // eslint-disable-next-line react/no-unknown-property
        about={content.url?.path}
        className={`node node--type-${content.bundle} node--id-${content.id} ngart media-${content.mediaType}`}
        css={styles.article}
        ref={articleTag}
      >
        <SponsoredBar content={content} articleRef={articleTag} />
        {content.immersiveLead && (
          <ImmersiveLead
            data={{ id: content.id, type: 'ImmersiveLead' }}
            content={content}
            isAboveTheFold={immersiveLeadIsAboveTheFold}
          />
        )}
        <ContentMainTitle content={content} />
        {heroImage}
        {getContentWithHeaders()}
      </article>
      {(content.mediaType === 'shortform' ||
        content.mediaType === 'video_playlist') &&
        content.recommendedVideos &&
        !content.hideVideoExplorer && (
          <div css={styles.relatedVideos}>
            <RelatedVideos
              videos={content.recommendedVideos}
              content={content}
            />
          </div>
        )}

      <Recommendation
        content={content}
        isTransported={isTransported}
        isExpanded={state.expanded}
      />
    </>
  );
});

ArticleInner.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  isTransported: PropTypes.bool.isRequired
};
